import { useState, useEffect, useRef, useCallback } from 'react';
import { getAudioEngine } from '../utils/audioEngine';
import { useZoomApp } from './useZoomApp';
import type { SoundName } from '../types';

/**
 * useBLSAudio - Hook for managing bilateral audio stimulation
 *
 * Features:
 * - Audio file preloading
 * - Bilateral playback with alternating panning (beep sounds)
 * - Continuous looping playback with real-time panning (continuous sounds)
 * - Sound mixing: play beep + continuous simultaneously
 * - Volume control
 * - Start/stop control
 */

interface UseBLSAudioReturn {
  isLoaded: boolean;
  isPlaying: boolean;
  start: (sounds: SoundName[], speed: number, muteForTherapist?: boolean, continuousSound?: SoundName | null) => Promise<void>;
  stop: () => void;
  setVolume: (volume: number) => void;
  updatePan: (panValue: number) => void;
  loadPrioritySounds: (sounds: SoundName[]) => Promise<void>;
  enableMixedMode: (sounds: SoundName[]) => void;
  disableMixedMode: () => void;
  addSound: (sound: SoundName) => Promise<void>;
  removeSound: (sound: SoundName) => void;
  contextState: AudioContextState | null;
  loadError: string | null;
}

export function useBLSAudio(): UseBLSAudioReturn {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [contextState, setContextState] = useState<AudioContextState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [currentSounds, setCurrentSounds] = useState<SoundName[]>([]);
  const audioEngineRef = useRef(getAudioEngine());
  const currentSoundsRef = useRef<SoundName[]>([]);
  const { userRole } = useZoomApp();

  // Keep currentSoundsRef in sync with currentSounds state
  useEffect(() => {
    currentSoundsRef.current = currentSounds;
  }, [currentSounds]);

  // Expose priority loading function for client
  const loadPrioritySounds = useCallback(async (sounds: SoundName[]) => {
    const engine = audioEngineRef.current;

    console.log('Priority loading sounds:', sounds);
    try {
      await engine.loadSounds(sounds);
      setIsLoaded(true);
      setContextState(engine.getContextState());
      setLoadError(null);
      console.log('Priority sounds loaded, starting background load...');

      // Start background loading remaining sounds (non-blocking)
      engine.loadRemainingInBackground();
    } catch (error) {
      console.error('Failed to load priority sounds:', error);
      setIsLoaded(false);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setLoadError(errorMessage);
    }
  }, []);

  // Load audio files on mount
  useEffect(() => {
    const engine = audioEngineRef.current;

    if (userRole === 'host') {
      // THERAPIST: Priority load all beep sounds (small files, fast ~2 seconds)
      const beepSounds: SoundName[] = ['tap', 'beep', 'heartbeat', 'rain', 'snap', 'wood', 'bamboo', 'drum', 'tibetan_ping'];

      console.log('Therapist: Priority loading beep sounds...');
      engine.loadSounds(beepSounds)
        .then(() => {
          setIsLoaded(true);
          setContextState(engine.getContextState());
          setLoadError(null);
          console.log('Beep sounds loaded, UI ready. Background loading continuous sounds...');

          // Background load continuous sounds (large files, slower)
          engine.loadRemainingInBackground();
        })
        .catch((error) => {
          console.error('Failed to load beep sounds:', error);
          setIsLoaded(false);
          const errorMessage = error instanceof Error ? error.message : String(error);
          setLoadError(errorMessage);
        });
    }
    // CLIENT: Don't load on mount, wait for state message

    // Clean up on unmount
    return () => {
      engine.stopAll();
    };
  }, [userRole]);

  const start = useCallback(async (
    sounds: SoundName[], // CHANGED: Now accepts array of sounds
    _speed: number, // No longer used - beeps triggered by position-based zero crossing
    muteForTherapist: boolean = false,
    _continuousSound: SoundName | null = null // Reserved for Phase 3: Sound mixing feature
  ) => {
    const engine = audioEngineRef.current;

    if (!isLoaded) {
      console.warn('Audio not loaded yet');
      return;
    }

    // Stop all current playback (continuous and discrete beeps)
    engine.stopAll();

    // Resume audio context (required for Safari and iframes)
    await engine.resumeContext();

    // Check if sound is 'off' - no audio playback
    if (sounds.length === 1 && sounds[0] === 'off') {
      console.log('Sound off - no audio playback');
      setCurrentSounds([]);
      setIsPlaying(true);
      return;
    }

    // Check if audio should be muted for therapist
    if (muteForTherapist && userRole === 'host') {
      console.log('Audio muted for therapist');
      // Don't store sounds (prevents beep triggering via updatePan)
      setCurrentSounds([]);
      // Don't start audio, but still set playing state for visual animation
      setIsPlaying(true);
      return;
    }

    // Store current sounds for pan updates (only if not muted)
    setCurrentSounds(sounds);

    // Start each selected sound
    for (const sound of sounds) {
      if (engine.isContinuousSound(sound)) {
        // Start continuous looping sound
        await engine.startContinuous(sound, engine.getVolume());
      }
      // For beep sounds, don't start anything here - beeps will be triggered
      // by updateBilateralPan() calls from the animation engine based on position
    }

    setIsPlaying(true);
    setContextState(engine.getContextState());
  }, [isLoaded, userRole]);

  const stop = useCallback(() => {
    const engine = audioEngineRef.current;

    // Stop all sounds (both continuous and discrete beeps)
    engine.stopAll();

    // Reset current sounds
    setCurrentSounds([]);

    setIsPlaying(false);
  }, []);

  const setVolume = useCallback((volume: number) => {
    const engine = audioEngineRef.current;
    engine.setVolume(volume);
  }, []);

  const updatePan = useCallback((panValue: number) => {
    const engine = audioEngineRef.current;
    const sounds = currentSoundsRef.current;

    // Trigger beep or update panning for ALL selected sounds
    for (const sound of sounds) {
      engine.updateBilateralPan(panValue, sound);
    }
  }, []);

  const enableMixedMode = useCallback((sounds: SoundName[]) => {
    const engine = audioEngineRef.current;
    engine.enableMixedMode(sounds);
  }, []);

  const disableMixedMode = useCallback(() => {
    const engine = audioEngineRef.current;
    engine.disableMixedMode();
  }, []);

  /**
   * Add a sound incrementally without stopping other sounds
   * For continuous sounds: starts the looping sound
   * For beep sounds: adds to tracking array (triggered by position)
   */
  const addSound = useCallback(async (sound: SoundName) => {
    const engine = audioEngineRef.current;

    // Don't add if already playing
    if (currentSoundsRef.current.includes(sound)) {
      return;
    }

    // Add to current sounds array
    setCurrentSounds(prev => [...prev, sound]);

    // For continuous sounds, start playback
    if (engine.isContinuousSound(sound)) {
      await engine.startContinuous(sound, engine.getVolume());
    }
    // For beep sounds, nothing to start - they're triggered by updateBilateralPan
  }, []);

  /**
   * Remove a sound incrementally without stopping other sounds
   * For continuous sounds: stops only that specific sound
   * For beep sounds: removes from tracking array
   */
  const removeSound = useCallback((sound: SoundName) => {
    const engine = audioEngineRef.current;

    // Remove from current sounds array
    setCurrentSounds(prev => prev.filter(s => s !== sound));

    // For continuous sounds, stop playback
    if (engine.isContinuousSound(sound)) {
      engine.stopContinuousSound(sound);
    }
    // For beep sounds, nothing to stop - just removing from array prevents future triggers
  }, []);

  return {
    isLoaded,
    isPlaying,
    start,
    stop,
    setVolume,
    updatePan,
    loadPrioritySounds,
    enableMixedMode,
    disableMixedMode,
    addSound,
    removeSound,
    contextState,
    loadError,
  };
}
